import { ApiQuest, ApiQuestDetail } from "griptonite-api-client/types";
import groupBy from "lodash/groupBy";
import { useEffect, useRef } from "react";

import useLocalStorage from "@/hooks/useLocalStorage";

type Warning = {
  message: string;
  tab: string;
};

// What the forms still need filled in before the API will publish the quest. The reward ladder
// only comes with the quest's detail, not the list entry that stands in for it while that loads, so
// an absent ladder isn't an empty one.
export default function getPublishWarnings(quest?: ApiQuest | ApiQuestDetail) {
  const warnings: Warning[] = [];

  if (!quest || !quest.isHidden || quest.type !== "LEADERBOARD_POINTS") {
    return { allWarnings: warnings, byTab: {} as Record<string, Warning[]> };
  }

  if (!quest.coverImage) {
    warnings.push({ message: "No cover image has been added", tab: "details" });
  }
  if (!quest.climbTypes?.length) {
    warnings.push({ message: "No disciplines have been chosen", tab: "details" });
  }
  if (!quest.startTime || !quest.endTime) {
    warnings.push({ message: "No month has been chosen", tab: "details" });
  }
  if ("pointsThresholds" in quest && !quest.pointsThresholds?.length) {
    warnings.push({ message: "No milestones have been added", tab: "rules" });
  }

  return { allWarnings: warnings, byTab: groupBy(warnings, "tab") };
}

// The tabs a new quest hasn't been through yet, whose warnings stay hidden until it has. Creating a
// quest hides them (see `useCreateQuestMutation`), and each later tab unhides the ones before it.
export const HIDEABLE_WARNING_TABS = ["rules", "promotion"] as const;

export const hideWarningsKey = (questUuid: string | undefined, tab: string) =>
  `quests:${questUuid}:hideWarnings:${tab}`;

export const useHideWarningsMap = (
  questUuid?: string,
): Record<string, boolean | null | undefined> => {
  const localStorageValues = useLocalStorage({
    keys: HIDEABLE_WARNING_TABS.map((tab) => hideWarningsKey(questUuid, tab)),
  });
  return Object.fromEntries(
    HIDEABLE_WARNING_TABS.map((tab) => [tab, localStorageValues[hideWarningsKey(questUuid, tab)]]),
  ) as Record<string, boolean | null | undefined>;
};

/**
 * Unhide warnings from the previous tab(s) when the component mounts.
 */
export function useUnhidePreviousTabWarningsOnMount(
  questUuid: string,
  previousTab: string,
  ...additionalTabs: string[]
) {
  const warningKeys = [previousTab, ...additionalTabs].map((tab) =>
    hideWarningsKey(questUuid, tab),
  );

  const { setItem: localStorageSetItem } = useLocalStorage({
    keys: warningKeys,
  });

  const warningKeysRef = useRef(warningKeys);

  useEffect(() => {
    warningKeysRef.current.forEach((key) => {
      localStorageSetItem(key, false);
    });
  }, [questUuid, localStorageSetItem]);
}
